import React, { useCallback, useEffect, useState } from 'react'
import { Container, Message } from 'rsuite'

import { useClientProjectContext } from '../context/ClientProjectContext'
import { useAuth } from '../context/AuthContext'
import { useClientContext } from '../context/ClientContext'
import { useProjectContext } from '../context/ProjectContext'
import OperatePeopleIcon from '@rsuite/icons/OperatePeople'
import ProjectIcon from '@rsuite/icons/Project'
import BarLineChartIcon from '@rsuite/icons/BarLineChart'
import { useNavigate } from 'react-router-dom'
import {
  WrapHStack,
  ValueInfoBigStyle,
  HeaderContainer,
} from './styles/Dashboard.styles'
import HeaderInfoDisplayer from './ui_reusable/HeaderInfoDisplayer'
import LoadingText from './ui_reusable/LoadingText'
import SimpleAccordion from './ui_reusable/SimpleAccordion'
import VersatileGrid from './ui_reusable/VersatileGrid'
import {
  GridCellBoldText,
  GridCellNormalText,
  GridCellSmallFadedText,
} from './ui_reusable/styles/VersatileGridUtils.styles'
import { VerySmallNormalTag } from './ui_reusable/styles/Tags.styles'
import CustomIcons from './ui_reusable/CustomIcons'
import theme from '../styles/theme'
import { ROUTED_REPORT_MODES } from './reports/types/ReportTypes'
import styled from '@emotion/styled'
import {
  HeaderLarge600Style,
  HeaderMedium600Style,
} from '../styles/Text.styles'
import useStandardToaster from './ui_reusable/StandardToaster'

const DASHBOARD_PAGE_SIZE = 5
/** Dashboard only surfaces the most recent N models/reports (ordering=-id). */
const DASHBOARD_MODELS_MAX = 50
const DASHBOARD_REPORTS_MAX = 50

const HorizontalContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 2rem;
  min-width: 0;

  @media (max-width: 1100px) {
    flex-direction: column;
  }
`

const HalfWidthContainer = styled.div`
  width: 50%;
  min-width: 0;

  @media (max-width: 1100px) {
    width: 100%;
  }
`

const ListContainer = styled.div`
  margin-bottom: 1rem;
`

const WelcomeContainer = styled.div`
  margin-bottom: 1.9rem;
  margin-top: 1rem;
  padding-left: 0.5rem;
`

const Dashboard = () => {
  useClientProjectContext()
  const { pushToast } = useStandardToaster()
  const { isAuthenticated, user, isAdmin, isClient, isClientUser } = useAuth()
  const { selectedClient, setSelectedClient } = useClientContext()
  const { selectedProject } = useProjectContext()
  const [stats, setStats] = useState()
  const [recentModels, setRecentModels] = useState([])
  const [recentReports, setRecentReports] = useState([])
  const [modelsTotal, setModelsTotal] = useState(0)
  const [reportsTotal, setReportsTotal] = useState(0)
  const [modelsPage, setModelsPage] = useState(1)
  const [reportsPage, setReportsPage] = useState(1)
  const [loadingModels, setLoadingModels] = useState(true)
  const [loadingReports, setLoadingReports] = useState(true)
  const [userName, setUserName] = useState('')
  const navigate = useNavigate()
  const [pendingEditNavigation, setPendingEditNavigation] = useState(null)

  useEffect(() => {
    if (isAuthenticated && isClient) {
      setSelectedClient(user.client)
    }
  }, [isAuthenticated, isClient, user, setSelectedClient])

  useEffect(() => {
    if (isClientUser) {
      navigate('/models/new')
    }
    /* eslint-disable-next-line */
  }, [isClientUser])

  useEffect(() => {
    const getStats = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/core/stats`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        )
        const res = await response.json()

        setStats(res.data)
      } catch (error) {
        console.error('Failed to fetch datasets:', error)
      }
    }

    getStats()
  }, [])

  const clientId = selectedClient?.id
  const projectId = selectedProject?.id

  // Reset pages when client/project filter changes.
  useEffect(() => {
    setModelsPage(1)
    setReportsPage(1)
  }, [clientId, projectId])

  useEffect(() => {
    const abortController = new AbortController()

    const fetchRecentModels = async () => {
      setLoadingModels(true)
      try {
        const params = new URLSearchParams({
          limit: String(DASHBOARD_PAGE_SIZE),
          page: String(modelsPage),
          ordering: '-id',
        })
        if (projectId) {
          params.set('project_id', String(projectId))
        }
        if (clientId && !projectId) {
          params.set('client_id', String(clientId))
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/artifacts/?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )
        const result = await response.json()

        setRecentModels(result.data || [])
        setModelsTotal(
          Math.min(result.total ?? result.data?.length ?? 0, DASHBOARD_MODELS_MAX),
        )
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching recent models:', error)
          setRecentModels([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoadingModels(false)
        }
      }
    }

    if (isAuthenticated) {
      fetchRecentModels()
    }

    return () => {
      abortController.abort()
    }
  }, [isAuthenticated, clientId, projectId, modelsPage])

  useEffect(() => {
    const abortController = new AbortController()

    const fetchRecentReports = async () => {
      setLoadingReports(true)
      try {
        const params = new URLSearchParams({
          limit: String(DASHBOARD_PAGE_SIZE),
          page: String(reportsPage),
          ordering: '-id',
        })
        if (projectId) {
          params.set('project_id', String(projectId))
        }
        if (clientId && !projectId) {
          params.set('client_id', String(clientId))
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/artifacts/reports?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )
        const result = await response.json()

        if (result.status !== 'success') {
          throw new Error(result)
        }

        setRecentReports(result.data || [])
        setReportsTotal(
          Math.min(
            result.total ?? result.data?.length ?? 0,
            DASHBOARD_REPORTS_MAX,
          ),
        )
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching recent reports:', error)
          setRecentReports([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoadingReports(false)
        }
      }
    }

    if (isAuthenticated) {
      fetchRecentReports()
    }

    return () => {
      abortController.abort()
    }
  }, [isAuthenticated, clientId, projectId, reportsPage])

  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/auth/userinfo/`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        )
        const res = await response.json()

        if (res.data && res.data.name) {
          setUserName(res.data.name)
        } else if (user && user.name) {
          setUserName(user.name)
        }
      } catch (error) {
        console.error('Error fetching user info:', error)
        if (user && user.name) {
          setUserName(user.name)
        }
      }
    }

    if (isAuthenticated) {
      getUserInfo()
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    if (pendingEditNavigation) {
      navigate('/models/build', {
        state: {
          solutionId:
            pendingEditNavigation.artifact.project_solution.solution.id,
          artifact: pendingEditNavigation.artifact,
          mode: pendingEditNavigation.mode,
          origin: pendingEditNavigation.origin,
        },
      })
      setPendingEditNavigation(null)
    }
  }, [pendingEditNavigation, navigate])

  const handleModelsPageChange = useCallback((page) => {
    const maxPage = Math.max(
      1,
      Math.ceil(Math.min(modelsTotal, DASHBOARD_MODELS_MAX) / DASHBOARD_PAGE_SIZE),
    )
    setModelsPage(Math.min(Math.max(page, 1), maxPage))
  }, [modelsTotal])

  const handleReportsPageChange = useCallback((page) => {
    const maxPage = Math.max(
      1,
      Math.ceil(
        Math.min(reportsTotal, DASHBOARD_REPORTS_MAX) / DASHBOARD_PAGE_SIZE,
      ),
    )
    setReportsPage(Math.min(Math.max(page, 1), maxPage))
  }, [reportsTotal])

  const handleViewReport = (report) => {
    const solutionName = report.solution?.toLowerCase()
    navigate(`/${solutionName}/reports/${report.id}`, {
      state: {
        id: report.id,
        origin: '/dashboard',
        mode: ROUTED_REPORT_MODES.VIEW_REPORT,
      },
    })
  }

  const handleEdit = async (model) => {
    const originalArtifact = recentModels.find((a) => a.id === model.id)

    if (!originalArtifact) {
      console.error('Original artifact not found for ID:', model.id)
      pushToast(
        <Message type="error">
          Could not edit model. Original data not found.
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }

    const solutionName = (
      originalArtifact.solution_type ||
      originalArtifact.project_solution?.solution?.name ||
      'impact'
    ).toLowerCase()
    const detailPath =
      solutionName === 'optimize'
        ? `optimize/${originalArtifact.id}`
        : `impact/${originalArtifact.id}`

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/${detailPath}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )
      if (!response.ok) {
        throw new Error('Failed to load artifact detail')
      }
      const result = await response.json()
      const detailArtifact = result.data || result

      setPendingEditNavigation({
        artifact: detailArtifact,
        mode: ROUTED_REPORT_MODES.EDIT_MODEL,
        origin: '/dashboard',
      })
    } catch (error) {
      console.error(error)
      pushToast(
        <Message type="error">
          Could not load model details for editing.
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  // NOTE(server-pagination): only backend-supported fields stay sortable.
  // `ArtifactListView` supports ordering on id/name/created_at/updated_at
  // (no run_at), `ArtifactReportList` on id/name/created_at/updated_at.
  const modelHeaders = [
    {
      title: 'Name',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: false,
      serverKey: 'name',
    },
    {
      title: 'Model',
      key: 'model',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Run date',
      key: 'run_at',
      type: 'datetime',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Edit',
      key: 'actions',
      type: 'none',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
  ]

  const reportHeaders = [
    {
      title: 'Name',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: false,
      serverKey: 'name',
    },
    {
      title: 'Model',
      key: 'solution',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Created date',
      key: 'created_at',
      type: 'datetime',
      isSortable: true,
      isSearchable: false,
      center: true,
      serverKey: 'created_at',
    },
    {
      title: 'View',
      key: 'actions',
      type: 'none',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
  ]

  return (
    <Container>
      {isAuthenticated && (
        <>
          <SimpleAccordion id={'dashboard-panel'} defaultActiveKey={[1]}>
            <SimpleAccordion.Panel eventKey={1} noCollapse={true}>
              {' '}
              <WelcomeContainer data-testid="welcome-section">
                <div css={HeaderLarge600Style}>
                  Welcome back, {userName || 'User'}
                </div>
              </WelcomeContainer>
              <HeaderContainer>
                <WrapHStack>
                  {isAdmin && (
                    <HeaderInfoDisplayer
                      title="Clients"
                      symbol={
                        <OperatePeopleIcon style={{ fontSize: '1.2em' }} />
                      }
                      columns={5}
                      data-testid="clients-section"
                    >
                      <LoadingText
                        css={ValueInfoBigStyle}
                        isLoading={!stats}
                        data-testid="clients-count"
                      >
                        {stats?.total_clients}
                      </LoadingText>
                    </HeaderInfoDisplayer>
                  )}
                  <HeaderInfoDisplayer
                    title="Projects"
                    symbol={<ProjectIcon style={{ fontSize: '1.2em' }} />}
                    columns={5}
                    data-testid="projects-section"
                  >
                    <LoadingText
                      css={ValueInfoBigStyle}
                      isLoading={!stats}
                      data-testid="projects-count"
                    >
                      {stats?.total_projects}
                    </LoadingText>
                  </HeaderInfoDisplayer>
                  <HeaderInfoDisplayer
                    title="Saved Models"
                    symbol={<BarLineChartIcon style={{ fontSize: '1.2em' }} />}
                    columns={5}
                    data-testid="saved-models-section"
                  >
                    <LoadingText
                      css={ValueInfoBigStyle}
                      isLoading={!stats}
                      data-testid="saved-models-count"
                    >
                      {stats?.total_artifacts}
                    </LoadingText>
                  </HeaderInfoDisplayer>
                  <HeaderInfoDisplayer
                    title="Datasets"
                    symbol={<BarLineChartIcon style={{ fontSize: '1.2em' }} />}
                    columns={5}
                    data-testid="datasets-section"
                  >
                    <LoadingText
                      css={ValueInfoBigStyle}
                      isLoading={!stats}
                      data-testid="datasets-count"
                    >
                      {stats?.total_datasets}
                    </LoadingText>
                  </HeaderInfoDisplayer>
                  <HeaderInfoDisplayer
                    title="Users"
                    symbol={<BarLineChartIcon style={{ fontSize: '1.2em' }} />}
                    columns={5}
                    data-testid="users-section"
                  >
                    <LoadingText
                      css={ValueInfoBigStyle}
                      isLoading={!stats}
                      data-testid="users-count"
                    >
                      {stats?.total_users}
                    </LoadingText>
                  </HeaderInfoDisplayer>
                </WrapHStack>
              </HeaderContainer>
              <HorizontalContainer>
                <HalfWidthContainer>
                  <ListContainer>
                    <VersatileGrid
                      data-testid="last-saved-models-grid"
                      gridContainerStyle={{
                        gridTemplateColumns: '1.5fr 0.7fr 0.7fr 0.5fr',
                      }}
                      headers={modelHeaders}
                      data={recentModels}
                      renderRow={(rowData) => [
                        <GridCellBoldText>{rowData.name}</GridCellBoldText>,
                        <GridCellNormalText>
                          <VerySmallNormalTag>
                            {rowData?.project_solution?.solution?.name}
                          </VerySmallNormalTag>
                        </GridCellNormalText>,
                        <GridCellSmallFadedText>
                          {rowData?.run_at && !isNaN(new Date(rowData.run_at))
                            ? new Date(rowData.run_at)
                                .toISOString()
                                .slice(0, 10)
                            : 'N/A'}
                        </GridCellSmallFadedText>,

                        <CustomIcons.PenIcon
                          color={theme.colors.fadedText}
                          size="xs"
                          onClick={() => handleEdit(rowData)}
                          style={{ cursor: 'pointer' }}
                        />,
                      ]}
                      headerInfo={
                        <div css={HeaderMedium600Style}>Last saved models</div>
                      }
                      maxDisplayedItems={DASHBOARD_PAGE_SIZE}
                      loading={loadingModels}
                      emptyStateMessage="No recent models found"
                      isSearchable={false}
                      loadingMessage={'Loading recent models...'}
                      serverSide
                      compactPagination
                      serverTotal={modelsTotal}
                      serverPage={modelsPage}
                      onPageChange={handleModelsPageChange}
                    />
                  </ListContainer>
                </HalfWidthContainer>
                <HalfWidthContainer>
                  <ListContainer>
                    <VersatileGrid
                      data-testid="last-saved-reports-grid"
                      gridContainerStyle={{
                        gridTemplateColumns: '1.5fr 0.7fr 0.7fr 0.5fr',
                      }}
                      headers={reportHeaders}
                      data={recentReports}
                      renderRow={(rowData) => [
                        <GridCellBoldText>{rowData.name}</GridCellBoldText>,
                        <GridCellNormalText>
                          <VerySmallNormalTag>
                            {rowData?.solution}
                          </VerySmallNormalTag>
                        </GridCellNormalText>,
                        <GridCellSmallFadedText>
                          {rowData?.created_at &&
                          !isNaN(new Date(rowData.created_at))
                            ? new Date(rowData.created_at)
                                .toISOString()
                                .slice(0, 10)
                            : 'N/A'}
                        </GridCellSmallFadedText>,

                        <CustomIcons.faEye
                          onClick={() => handleViewReport(rowData)}
                          color={theme.colors.fadedText}
                          solid={false}
                          size="xs"
                          style={{ cursor: 'pointer' }}
                        />,
                      ]}
                      headerInfo={
                        <div css={HeaderMedium600Style}>Last saved reports</div>
                      }
                      maxDisplayedItems={DASHBOARD_PAGE_SIZE}
                      loading={loadingReports}
                      emptyStateMessage="No recent reports found"
                      isSearchable={false}
                      loadingMessage={'Loading recent reports...'}
                      serverSide
                      compactPagination
                      serverTotal={reportsTotal}
                      serverPage={reportsPage}
                      onPageChange={handleReportsPageChange}
                    />
                  </ListContainer>
                </HalfWidthContainer>
              </HorizontalContainer>
            </SimpleAccordion.Panel>
          </SimpleAccordion>
        </>
      )}
    </Container>
  )
}

export default Dashboard
